import type { Column, Registrant } from "./api.js";

export type FilterValue =
  | { kind: "text"; q: string }
  | { kind: "set"; values: string[] }
  | { kind: "bool"; value: boolean }
  | { kind: "range"; min?: number; max?: number }
  | { kind: "dateRange"; from?: string; to?: string };

export type FilterMap = Record<string, FilterValue>;
export type SortSpec = { columnId: string; dir: "asc" | "desc" };

const customPrefix = "cf_";

export function getCellValue(row: Registrant, col: Column): unknown {
  if (col.id.startsWith(customPrefix)) {
    return row.custom[col.id.slice(customPrefix.length)];
  }
  return (row as unknown as Record<string, unknown>)[col.id];
}

function asString(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

function matchesFilter(row: Registrant, col: Column, filter: FilterValue): boolean {
  const value = getCellValue(row, col);
  switch (filter.kind) {
    case "text": return !filter.q || asString(value).toLowerCase().includes(filter.q.toLowerCase());
    case "bool": return Boolean(value) === filter.value;
    case "set": {
      if (filter.values.length === 0) return true;
      if (Array.isArray(value)) return value.some((v) => filter.values.includes(String(v)));
      return filter.values.includes(asString(value));
    }
    case "range": {
      const n = typeof value === "number" ? value : Number(value);
      if (Number.isNaN(n)) return false;
      if (filter.min !== undefined && n < filter.min) return false;
      if (filter.max !== undefined && n > filter.max) return false;
      return true;
    }
    case "dateRange": {
      const s = asString(value);
      if (!s) return false;
      if (filter.from && s < filter.from) return false;
      if (filter.to && s > filter.to) return false;
      return true;
    }
  }
}

export function applyFilters(rows: readonly Registrant[], columns: readonly Column[], filters: FilterMap): Registrant[] {
  const colsById = new Map(columns.map((c) => [c.id, c]));
  const entries = Object.entries(filters).filter(([id]) => colsById.has(id));
  if (!entries.length) return [...rows];
  return rows.filter((row) => entries.every(([id, f]) => {
    const col = colsById.get(id);
    return col ? matchesFilter(row, col, f) : true;
  }));
}

function compareValues(a: unknown, b: unknown): number {
  const aNull = a === null || a === undefined || a === "";
  const bNull = b === null || b === undefined || b === "";
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

export function applySort(rows: readonly Registrant[], columns: readonly Column[], sort: SortSpec | null): Registrant[] {
  if (!sort) return [...rows];
  const col = columns.find((c) => c.id === sort.columnId);
  if (!col) return [...rows];
  const dir = sort.dir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => compareValues(getCellValue(a, col), getCellValue(b, col)) * dir);
}

export function activeFilterCount(filters: FilterMap): number {
  let n = 0;
  for (const f of Object.values(filters)) {
    switch (f.kind) {
      case "text": if (f.q) n++; break;
      case "bool": n++; break;
      case "set": if (f.values.length) n++; break;
      case "range": if (f.min !== undefined || f.max !== undefined) n++; break;
      case "dateRange": if (f.from || f.to) n++; break;
    }
  }
  return n;
}
